export const parseCurrency = (priceStr) => {
  /**
   * Magic regex that matches:
   * currency: 'symboo'
   * currency + 1 space + value e.g. EUR 100.00,00
   * value + 1 space + currency e.g. 100,00.00 GBR
   */
  const currencyMatcher = /^(?:([A-Z]{3}) ?)?(?:([^\d ]+?) ?)?(((?:\d{1,3}([,. ’'\u00A0]))*?\d{1,})(([,.])\d{1,2})?)(?: ?([^\d ]+?))??(?: ?([A-Z]{3}))?$/;

  /**
   * Validate group of numbers up to 3 digits
   */
  const gr = /^\d{1,3}([,. ’'\u00A0]\d{3})*$/;

  if (!priceStr || !priceStr.match) return null;

  /**
   * Example of what const match should return if priceStr === 123.456,00:
   * ["123.456,00", undefined, undefined, "123.456,00", "123.456", ".", ",00", ","]
   */
  const match = priceStr.match(currencyMatcher);

  if (!match) return null;

  const groupSeparator = match[5] || '';
  const decimalSeparator = match[7] || '';

  if (groupSeparator === decimalSeparator && decimalSeparator) {
    return null;
  }
  const integer = match[4];

  if (groupSeparator && !integer.match(gr)) {
    return null;
  }
  let value = match[3];

  if (!value) return null;
  if (groupSeparator) {
    value = value.replace(RegExp(`\\${groupSeparator}`, 'g'), '');
  }
  if (decimalSeparator) {
    value = value.replace(decimalSeparator, '.');
  }
  const numericVal = +value;

  /**
   * Number.isNaN() and isNaN() has a teeny tiny difference, more info:
   * https://gitlab.com/raisin-global/raisin-gmbh/javascript-libs/ui-lib/-/merge_requests/558#note_447176176
   */

  if (typeof numericVal !== 'number' || Number.isNaN(numericVal)) {
    return null;
  }

  return {
    raw: priceStr,
    value: numericVal,
    integer: integer || '',
    decimals: match[6] || '',
    currency: match[1] || match[9] || '',
    symbol: match[2] || match[8] || '',
    decimalSeparator,
    groupSeparator,
  };
};

export const commaDecimalSeparatorCountries = ['DEU', 'AUT', 'ESP', 'FRA', 'NLD'];

export const amountFormatByCountry = (country, amount) => {
  if (commaDecimalSeparatorCountries.includes(country)) {
    return amount.replace(/\./g, '').replace(/,/g, '.');
  }

  return amount.replace(/,/g, '');
};

export const getNumberSign = (amount, onPositive = '+', onZero = '') => {
  if (amount > 0) {
    return onPositive;
  } else if (amount < 0) {
    return '–';
  }

  return onZero;
};

export const getDateTime = (timestamp = null) => {
  const date = timestamp ? new Date(timestamp) : new Date();
  const hours = `0${date.getHours()}`.slice(-2);
  const minutes = `0${date.getMinutes()}`.slice(-2);

  return {
    date: [date.getFullYear(), date.getMonth() + 1, date.getDate()],
    time: `${hours}:${minutes}`,
  };
};

/**
 * Converts a formatted string with currency into a javascript number
 * Example:
 * currencyAmountToNumber('2.000,01 EUR') = 2000.01
 * currencyAmountToNumber('GBP 2,000.01') = 2000.01
 * currencyAmountToNumber('EUR 2,000.01') = 2000.01
 */

export const currencyAmountToNumber = (value) => {
  if (!value) return 0;

  return +parseCurrency(value)?.value || 0;
};

const timeUnits = [
  ['Y', 1000 * 60 * 60 * 24 * 365], // years
  ['M', 1000 * 60 * 60 * 24 * 30], // months
  ['D', 1000 * 60 * 60 * 24], // days
  ['H', 1000 * 60 * 60], // hours
  ['m', 1000 * 60], // minutes
  ['s', 1000], // seconds
  ['S', 1], // million seconds
];

export const formatTimeObj = (duration, format) => {
  let leftDuration = duration;

  return timeUnits.reduce((current, [name, unit]) => {
    if (format.indexOf(name) !== -1) {
      const value = Math.floor(leftDuration / unit);

      leftDuration -= value * unit;
      const [currentUnit] = format.match(new RegExp(`${name}+`, 'g'));
      const len = currentUnit.length;
      const formattedValue = value.toString().padStart(len, '0');

      return { ...current, [currentUnit]: formattedValue };
    }

    return current;
  }, {});
};
